import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowRight, Check, Loader2, X } from 'lucide-react'
import { PageMeta } from '@/components/seo/PageMeta'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { HeroReveal, InteractiveDots } from '@/components/motion'
import { cn } from '@/lib/utils'
import { betaService } from '@/lib/api/services/beta'

type VerificationState = 'loading' | 'success' | 'already-verified' | 'error'

export default function BetaVerify() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [state, setState] = useState<VerificationState>('loading')
  const [countdown, setCountdown] = useState(5)

  useEffect(() => {
    if (!token) {
      setState('error')
      return
    }

    const verifyEmail = async () => {
      try {
        const response = await betaService.verifyEmail(token)
        if (response.alreadyVerified) {
          setState('already-verified')
        } else {
          setState('success')
        }
      } catch {
        setState('error')
      }
    }

    verifyEmail()
  }, [token])

  // Countdown and redirect on success
  useEffect(() => {
    if (state === 'success' || state === 'already-verified') {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            navigate('/')
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [state, navigate])

  const content: Record<
    VerificationState,
    { title: string; highlight: string; text: string; tile: string; icon: React.ReactNode }
  > = {
    loading: {
      title: 'Verifying your',
      highlight: 'email...',
      text: 'Please wait while we confirm your registration.',
      tile: 'bg-[var(--color-landing-gray-100)] text-[var(--color-landing-gray-900)] dark:bg-white/[0.06] dark:text-white',
      icon: <Loader2 className="h-1/3 w-1/3 animate-spin" strokeWidth={1.5} />,
    },
    success: {
      title: 'Email',
      highlight: 'verified!',
      text: "Welcome to the Templatean beta program! We've sent you a welcome email with more details. You'll be among the first to know when we're ready to onboard new users.",
      tile: 'bg-[var(--color-landing-accent)] text-white',
      icon: <Check className="h-1/3 w-1/3" strokeWidth={2} />,
    },
    'already-verified': {
      title: 'Already',
      highlight: 'verified.',
      text: "Your email has already been verified. You're all set for the beta program!",
      tile: 'bg-[var(--color-landing-secondary)] text-white',
      icon: <Check className="h-1/3 w-1/3" strokeWidth={2} />,
    },
    error: {
      title: 'Verification',
      highlight: 'failed.',
      text: "We couldn't verify your email. The link may have expired or is invalid. Please try registering again or contact support if the problem persists.",
      tile: 'bg-[#ff00f2] text-white',
      icon: <X className="h-1/3 w-1/3" strokeWidth={2} />,
    },
  }

  const view = content[state]
  const done = state === 'success' || state === 'already-verified'

  return (
    <>
      <PageMeta
        title="Verify Email | Templatean Beta"
        description="Verify your email to complete your beta registration."
      />

      <LandingLayout>
        <section className="landing-container grid min-h-[calc(100svh-72px)] items-center gap-12 py-16 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:gap-24">
          <div aria-live="polite">
            <HeroReveal key={state}>
              <span aria-hidden className={cn('mb-8 flex h-16 w-16 items-center justify-center lg:hidden', view.tile)}>
                {view.icon}
              </span>
              <h1 className="landing-text-hero text-[var(--color-landing-gray-900)] dark:text-white">
                {view.title}{' '}
                <span className={cn('landing-highlight', state === 'already-verified' && 'landing-highlight--secondary', state === 'error' && '!bg-[#ff00f2]', state === 'loading' && '!bg-[var(--color-landing-gray-900)] dark:!bg-white dark:!text-[var(--color-landing-primary)]')}>
                  {view.highlight}
                </span>
              </h1>
              <p className="mt-10 max-w-[52ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                {view.text}
              </p>

              {done && (
                <div className="mt-12 flex flex-wrap items-center gap-6">
                  <Link
                    to="/"
                    className={cn(
                      'landing-cta landing-cta--lg landing-btn-sweep',
                      state === 'success' ? 'landing-cta--solid' : 'landing-cta--outline'
                    )}
                  >
                    Go to Home Now
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <p className="text-sm tabular-nums text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                    Redirecting to home in {countdown} seconds...
                  </p>
                </div>
              )}

              {state === 'error' && (
                <div className="mt-12 flex flex-wrap gap-4">
                  <Link to="/join-beta" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep">
                    Register Again
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <Link to="/contact" className="landing-cta landing-cta--outline landing-cta--lg landing-btn-sweep">
                    Contact Support
                  </Link>
                </div>
              )}
            </HeroReveal>
          </div>

          {/* Status tile: a square colour block with the static halftone */}
          <HeroReveal delay={0.15} className="hidden lg:block">
            <div
              aria-hidden
              className={cn(
                'relative flex aspect-square w-full max-w-[34rem] items-center justify-center overflow-hidden transition-colors duration-500 lg:ml-auto',
                view.tile
              )}
            >
              {state !== 'loading' && <InteractiveDots spacing={16} maxSize={3.6} />}
              <span className="relative z-10 flex h-full w-full items-center justify-center">{view.icon}</span>
            </div>
          </HeroReveal>
        </section>
      </LandingLayout>
    </>
  )
}
